import { useEffect, useState } from 'react';
import { Plus, Trash2, Pencil, Loader2, Eye, EyeOff, Sparkles, ImageOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from '@/components/ui/sonner';
import {
  adminListJournalPosts, adminCreateJournalPost, adminUpdateJournalPost, adminDeleteJournalPost,
  adminPublishJournalPost, adminUnpublishJournalPost, adminSuggestJournalCover,
} from '@/lib/api';
import { ADMIN_DASHBOARD, ADMIN_JOURNAL_TABLE } from '@/constants/testIds';
import { JournalAutomationCard } from '@/components/admin/JournalAutomationCard';

const EMPTY_FORM = { slug: '', title: '', excerpt: '', content: '', cover_image_url: '', tags: '' };

export const AdminJournalTab = () => {
  const [posts, setPosts] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [suggestingCover, setSuggestingCover] = useState(false);

  const refresh = () => adminListJournalPosts().then(setPosts).catch(() => setPosts([]));

  useEffect(() => {
    refresh();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p.slug);
    setForm({ ...p, tags: (p.tags || []).join(', ') });
    setDialogOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form, tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean) };
    try {
      if (editing) {
        const { slug, ...updates } = payload;
        await adminUpdateJournalPost(editing, updates);
        toast.success('Post updated.');
      } else {
        await adminCreateJournalPost(payload);
        toast.success('Post created.');
      }
      setDialogOpen(false);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save post.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`Delete post "${p.title}"? This cannot be undone.`)) return;
    try {
      await adminDeleteJournalPost(p.slug);
      toast.success('Post deleted.');
      refresh();
    } catch (err) {
      toast.error('Failed to delete post.');
    }
  };

  const handleSuggestCover = async () => {
    if (!form.title.trim()) {
      toast.error('Add a title first so the AI knows what to depict.');
      return;
    }
    setSuggestingCover(true);
    try {
      const { cover_image_url } = await adminSuggestJournalCover({ title: form.title, excerpt: form.excerpt });
      setForm((f) => ({ ...f, cover_image_url }));
      toast.success('Cover image suggested. Save to keep it, or suggest again for another take.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not generate a cover image right now.');
    } finally {
      setSuggestingCover(false);
    }
  };

  const togglePublish = async (p) => {
    try {
      if (p.status === 'draft') {
        await adminPublishJournalPost(p.slug);
        toast.success('Post published to /journal.');
      } else {
        await adminUnpublishJournalPost(p.slug);
        toast.success('Post moved back to drafts.');
      }
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not change publish status.');
    }
  };

  return (
    <div>
      <JournalAutomationCard onDraftGenerated={refresh} />

      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-[hsl(var(--lxra-muted))]">Manage articles shown on the /journal page. AI drafts always land here first for your review.</p>
        <Button
          onClick={openCreate}
          data-testid={ADMIN_DASHBOARD.createJournalButton}
          className="h-10 px-4 rounded-lg bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold-hover))] text-xs uppercase tracking-[0.1em] font-semibold"
        >
          <Plus className="h-4 w-4 mr-1.5" />
          New Post
        </Button>
      </div>

      {!posts && <Skeleton className="h-64 rounded-2xl bg-[#141416]" />}

      {posts && posts.length === 0 && (
        <div className="rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] p-10 text-center">
          <p className="text-sm text-[hsl(var(--lxra-muted))]">No posts yet. Write one manually, or let the Autonomous Blog Writer above draft the first one.</p>
        </div>
      )}

      {posts && posts.length > 0 && (
        <div className="rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] overflow-hidden overflow-x-auto" data-testid={ADMIN_JOURNAL_TABLE.root}>
          <Table>
            <TableHeader>
              <TableRow className="border-[hsl(var(--lxra-border))] hover:bg-transparent">
                <TableHead className="text-[hsl(var(--lxra-muted))]">Title</TableHead>
                <TableHead className="text-[hsl(var(--lxra-muted))]">Slug</TableHead>
                <TableHead className="text-[hsl(var(--lxra-muted))]">Status</TableHead>
                <TableHead className="text-[hsl(var(--lxra-muted))] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {posts.map((p) => {
                const missingCover = p.status === 'draft' && !(p.cover_image_url || '').trim();
                return (
                <TableRow key={p.slug} className="border-[hsl(var(--lxra-border))] hover:bg-[#141416]">
                  <TableCell className="text-[hsl(var(--lxra-fg))]">
                    <div className="flex items-center gap-2">
                      {p.title}
                      {p.ai_generated && (
                        <span data-testid={ADMIN_JOURNAL_TABLE.aiBadge(p.slug)} title="AI-drafted">
                          <Sparkles className="h-3.5 w-3.5 text-[hsl(var(--lxra-gold))]" />
                        </span>
                      )}
                      {missingCover && (
                        <span
                          data-testid={ADMIN_JOURNAL_TABLE.missingCoverHint(p.slug)}
                          title="Missing cover image — add one before publishing"
                          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.06em] text-amber-400/90 border border-amber-400/30 bg-amber-400/10 rounded-full px-1.5 py-0.5"
                        >
                          <ImageOff className="h-3 w-3" /> No cover
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-[hsl(var(--lxra-muted))]">{p.slug}</TableCell>
                  <TableCell>
                    <Badge
                      data-testid={ADMIN_JOURNAL_TABLE.statusBadge(p.slug)}
                      className={p.status === 'draft'
                        ? 'bg-transparent text-[hsl(var(--lxra-muted))] border border-[hsl(var(--lxra-border))] font-normal'
                        : 'bg-[hsl(var(--lxra-gold)/0.14)] text-[hsl(var(--lxra-cream))] border border-[hsl(var(--lxra-gold)/0.35)] font-normal'}
                    >
                      {p.status === 'draft' ? 'Draft' : 'Published'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => togglePublish(p)}
                      disabled={missingCover}
                      data-testid={p.status === 'draft' ? ADMIN_JOURNAL_TABLE.publishButton(p.slug) : ADMIN_JOURNAL_TABLE.unpublishButton(p.slug)}
                      className="text-[hsl(var(--lxra-muted))] hover:text-[hsl(var(--lxra-gold))] hover:bg-[#1a1a1c] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-[hsl(var(--lxra-muted))]"
                      title={missingCover ? 'Add a cover image before publishing' : (p.status === 'draft' ? 'Publish' : 'Unpublish')}
                    >
                      {p.status === 'draft' ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => openEdit(p)} data-testid={`admin-journal-edit-${p.slug}`} className="text-[hsl(var(--lxra-muted))] hover:text-[hsl(var(--lxra-gold))] hover:bg-[#1a1a1c]">
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => handleDelete(p)} data-testid={`admin-journal-delete-${p.slug}`} className="text-[hsl(var(--lxra-muted))] hover:text-red-400 hover:bg-red-400/10">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#0F0F10] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[hsl(var(--lxra-fg))]">{editing ? 'Edit Post' : 'New Post'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            {!editing && (
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.1em] text-[hsl(var(--lxra-muted))]">Slug (URL id)</Label>
                <Input required value={form.slug} onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') }))} className="bg-[#141416] border-[hsl(var(--lxra-border))] h-10" />
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.1em] text-[hsl(var(--lxra-muted))]">Title</Label>
              <Input required value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} className="bg-[#141416] border-[hsl(var(--lxra-border))] h-10" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.1em] text-[hsl(var(--lxra-muted))]">Excerpt</Label>
              <Textarea value={form.excerpt} onChange={(e) => setForm((f) => ({ ...f, excerpt: e.target.value }))} className="bg-[#141416] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))]" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.1em] text-[hsl(var(--lxra-muted))]">Content (paragraphs separated by blank lines)</Label>
              <Textarea rows={6} value={form.content} onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))} className="bg-[#141416] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))]" />
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label className="text-xs uppercase tracking-[0.1em] text-[hsl(var(--lxra-muted))]">Cover Image URL</Label>
                <button
                  type="button"
                  onClick={handleSuggestCover}
                  disabled={suggestingCover}
                  data-testid={ADMIN_JOURNAL_TABLE.suggestCoverButton}
                  className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.08em] text-[hsl(var(--lxra-gold))] hover:text-[hsl(var(--lxra-gold-hover))] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {suggestingCover ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                  {suggestingCover ? 'Generating...' : 'Suggest with AI'}
                </button>
              </div>
              <Input required value={form.cover_image_url} onChange={(e) => setForm((f) => ({ ...f, cover_image_url: e.target.value }))} className="bg-[#141416] border-[hsl(var(--lxra-border))] h-10" />
              {form.cover_image_url && (
                <img src={form.cover_image_url} alt="" className="mt-2 h-28 w-full rounded-lg object-cover border border-[hsl(var(--lxra-border))]" />
              )}
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.1em] text-[hsl(var(--lxra-muted))]">Tags (comma separated)</Label>
              <Input value={form.tags} onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))} className="bg-[#141416] border-[hsl(var(--lxra-border))] h-10" />
            </div>
            <DialogFooter>
              <Button type="submit" disabled={saving} className="h-10 px-5 rounded-lg bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold-hover))] text-xs uppercase tracking-[0.1em] font-semibold">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? 'Save Changes' : 'Create Post'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
